import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2, Monitor } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/u6f2dm1t_CompliSun.png';
const FOX_MASCOT = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/28ykjv9m_Transparent%20BG%20Fox.png';

const VerifyMagicLink = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyMagicLink } = useAuth();
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState('');
  const [remembered, setRemembered] = useState(false);
  const verifyAttempted = useRef(false);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setError('Invalid magic link. No token provided.');
      return;
    }

    // Prevent double execution (React StrictMode)
    if (verifyAttempted.current) return;
    verifyAttempted.current = true;

    const verify = async () => {
      try {
        // Read remember preference set on Login page
        const rememberPref = localStorage.getItem('remember_device_pref') === 'true';
        localStorage.removeItem('remember_device_pref');

        const user = await verifyMagicLink(token, rememberPref);
        setStatus('success');
        setRemembered(rememberPref);
        toast.success(`Welcome${user.name ? `, ${user.name.split(' ')[0]}` : ''}!`);
        
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1500);
      } catch (err) {
        setStatus('error');
        const message = err.response?.data?.detail || 'Failed to verify magic link';
        setError(message);
        toast.error(message);
      }
    };

    verify();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-[#1A2B5E] flex items-center justify-center p-4" data-testid="verify-magic-link-page">
      <div className="w-full max-w-md text-center">
        <img src={LOGO_URL} alt="CompliSun" className="w-16 h-16 mx-auto mb-6" />
        
        <div className="bg-white rounded-xl p-8 shadow-xl">
          {status === 'verifying' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#40CFDE]/10 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-[#40CFDE] animate-spin" data-testid="verify-loading-spinner" />
              </div>
              <h2 className="text-2xl font-bold text-[#1A2B5E] font-['Amaranth']">
                Verifying Magic Link...
              </h2>
              <p className="text-navy/60 mt-2">
                Just a moment while we sign you in
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#37C563]/10 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-[#37C563]" data-testid="verify-success-icon" />
              </div>
              <h2 className="text-2xl font-bold text-[#1A2B5E] font-['Amaranth']">
                You're In!
              </h2>
              <p className="text-navy/60 mt-2">
                Redirecting to your dashboard...
              </p>
              {remembered && (
                <div className="mt-3 flex items-center justify-center gap-2 text-sm text-[#40CFDE]">
                  <Monitor className="w-4 h-4" />
                  <span>This device is now remembered</span>
                </div>
              )}
              <img src={FOX_MASCOT} alt="Fox" className="w-20 h-20 mx-auto mt-6" />
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-500" data-testid="verify-error-icon" />
              </div>
              <h2 className="text-2xl font-bold text-[#1A2B5E] font-['Amaranth']">
                Link Invalid or Expired
              </h2>
              <p className="text-navy/60 mt-2">
                {error}
              </p>
              <Button
                className="mt-6 bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90"
                onClick={() => navigate('/login')}
                data-testid="request-new-link-btn"
              >
                Request New Magic Link
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyMagicLink;
